import { getRecordFormat, hasStrength, type CognitiveDistortion, type ThoughtRecord, type VoiceLog } from '@adhd/shared';
import { useEffect, useState } from 'react';
import { Link, useNavigate, useParams } from 'react-router-dom';
import { CrisisNoticeCard, type CrisisNotice } from '../components/CrisisNotice.js';
import { ArrowLeftIcon, PrinterIcon } from '../components/Icons.js';
import { RatingSlider } from '../components/RatingSlider.js';
import { FeelingsEditor, splitLines, TrapPicker, type FeelingBefore } from '../components/WorksheetEditors.js';
import { RecordMeds } from '../components/RecordMeds.js';
import { TalkOffer } from '../components/TalkOffer.js';
import { api, ApiError } from '../lib/api.js';
import { useAi } from '../lib/ai.js';

/**
 * One thought record, in full.
 *
 * If it was never finished, this screen lets you finish it here rather than
 * making you start again — which is the difference between a half-written
 * record being useful and being a small monument to giving up. The closing
 * step is the one that does the work, so the path back to it should be short.
 */
export function ThoughtRecordDetail(): JSX.Element {
  
  const { id } = useParams<{ id: string }>();
  const navigate = useNavigate();
  const [record, setRecord] = useState<ThoughtRecord | null>(null);
  // Above every early return, with the other hooks. Called further down it
  // was a rules-of-hooks violation: React counts hooks per render, and a
  // render that exits early calls fewer of them. Latent rather than live —
  // both screens render fine today — but it fires the moment anything after
  // it becomes conditional, and on the owner's side of this app the routes
  // sit outside the ErrorBoundary, so the failure is a white page.
  const { canConverse } = useAi();
  const [source, setSource] = useState<VoiceLog | null>(null);
  const [error, setError] = useState<string | null>(null);

  const [response, setResponse] = useState('');
  /*
   * Null until he moves it; finishing then saves "no change" from where he
   * started. It was 50 when he had never rated the belief at all — a worksheet
   * drafted from an entry that did not say — and finishing saved that 50 as his
   * (13 Sep). With no belief to start from it reads Not said yet and saves none.
   */
  const [beliefAfterRaw, setBeliefAfterRaw] = useState<number | null>(null);
  const [afterIntensities, setAfterIntensities] = useState<Record<string, number>>({});
  const [busy, setBusy] = useState(false);
  // The traps, for their names in the "Thinking traps" section and for the
  // picker when changing it. A failure leaves the keys readable (underscores as
  // spaces) rather than hiding the section.
  const [distortions, setDistortions] = useState<CognitiveDistortion[]>([]);
  const trapNames: Record<string, string> = Object.fromEntries(distortions.map((d) => [d.key, d.name]));

  /*
   * Changing a saved worksheet.
   *
   * Nothing but the response and the re-ratings could be changed here, so a
   * worksheet drafted from a journal entry kept the model's sorting of his
   * evidence and its trap picks for good (13 Sep). Every column this screen
   * shows is now his to change, through the PATCH that always accepted them.
   * The re-ratings stay with finishing it, below.
   */
  const [editing, setEditing] = useState(false);
  const [edit, setEdit] = useState<{
    situation: string;
    thought: string;
    beliefBefore: number | null;
    emotions: FeelingBefore[];
    evidenceFor: string;
    evidenceAgainst: string;
    picked: string[];
    appPicked: string[];
    response: string;
  } | null>(null);
  const [crisis, setCrisis] = useState<CrisisNotice | null>(null);

  useEffect(() => {
    void api
      .distortions()
      .then(setDistortions)
      .catch(() => {});
  }, []);

  useEffect(() => {
    if (!id) return;
    api
      .getThoughtRecord(id)
      .then((r) => {
        setRecord(r);
        setResponse(r.response ?? '');
        setBeliefAfterRaw(r.beliefAfter);
        // A record drafted from a journal entry has a whole conversation behind
        // it that the seven columns had to throw away. Fetch the entry so the
        // way back to it is one tap, not a hunt through the log.
        //
        // Failure here is silent on purpose: the record is the thing on screen
        // and it is complete without this. Deleting the entry clears the link
        // at the database (ON DELETE SET NULL, with foreign keys enforced), so
        // a 404 here means something unexpected rather than something normal —
        // but still nothing worth putting an error banner in front of someone
        // who came here to finish a thought.
        if (r.voiceLogId) {
          api
            .getJournalEntry(r.voiceLogId)
            .then(setSource)
            .catch(() => setSource(null));
        }
      })
      .catch((e: unknown) =>
        setError(e instanceof ApiError ? e.message : 'Could not load that record.'),
      );
  }, [id]);

  // A failed save while changing it keeps the changes on screen, with the banner
  // above them; any other failure has nothing to show.
  if (error && !editing) return <div className="banner banner-error">{error}</div>;
  if (!record) {
    return (
      <div style={{ display: 'grid', placeItems: 'center', minHeight: '50dvh' }}>
        <div className="spinner" aria-label="Loading" />
      </div>
    );
  }

  const evidenceFor = record.evidence.filter((e) => e.side === 'for');
  const evidenceAgainst = record.evidence.filter((e) => e.side === 'against');
  // Label the columns the way the worksheet that produced them did. Showing
  // "Evidence it's true" on a decatastrophising record would be nonsense.
  const format = getRecordFormat(record.format);
  const lastStep = format.steps[format.steps.length - 1]!;

  async function finish(): Promise<void> {
    if (!id || !record) return;
    setBusy(true);
    try {
      const updated = await api.updateThoughtRecord(id, {
        response: response.trim() || null,
        beliefAfter: beliefAfterRaw ?? record.beliefBefore,
        emotions: record.emotions.map((e) => ({
          emotion: e.emotion,
          intensityBefore: e.intensityBefore,
          intensityAfter: afterIntensities[e.emotion] ?? e.intensityAfter ?? e.intensityBefore,
        })),
      });
      setRecord(updated);
    } catch (e) {
      setError(e instanceof ApiError ? e.message : 'Could not save that.');
    } finally {
      setBusy(false);
    }
  }

  function startEditing(): void {
    if (!record) return;
    setEdit({
      situation: record.situation,
      thought: record.automaticThought,
      beliefBefore: record.beliefBefore,
      emotions: record.emotions.map((e) => ({ emotion: e.emotion, intensityBefore: e.intensityBefore })),
      evidenceFor: record.evidence.filter((e) => e.side === 'for').map((e) => e.text).join('\n'),
      evidenceAgainst: record.evidence.filter((e) => e.side === 'against').map((e) => e.text).join('\n'),
      picked: [...record.distortions],
      appPicked: record.appDistortions.filter((k) => record.distortions.includes(k)),
      response: record.response ?? '',
    });
    setEditing(true);
  }

  async function saveChanges(): Promise<void> {
    if (!id || !record || !edit) return;
    setBusy(true);
    setError(null);
    try {
      // A feeling he kept keeps its re-rating; one he added has none yet.
      const after = new Map(record.emotions.map((e) => [e.emotion, e.intensityAfter]));
      const updated = await api.updateThoughtRecord(id, {
        situation: edit.situation.trim(),
        automaticThought: edit.thought.trim(),
        beliefBefore: edit.beliefBefore,
        emotions: edit.emotions.filter(hasStrength).map((e) => ({
          emotion: e.emotion,
          intensityBefore: e.intensityBefore,
          intensityAfter: after.get(e.emotion) ?? null,
        })),
        evidence: [
          ...splitLines(edit.evidenceFor).map((text) => ({ side: 'for' as const, text })),
          ...splitLines(edit.evidenceAgainst).map((text) => ({ side: 'against' as const, text })),
        ],
        distortions: edit.picked,
        // Narrows the app's mark to the picks he left; the server never widens it.
        appDistortions: edit.picked.filter((k) => edit.appPicked.includes(k)),
        // Only a finished worksheet shows its response here; an unfinished one
        // is finished below, as before.
        ...(record.isComplete ? { response: edit.response.trim() || null } : {}),
      });
      setRecord(updated);
      // An unfinished worksheet's answer is being written in "Finish it now",
      // and this edit did not send it — leave what he has typed there alone.
      if (record.isComplete) setResponse(updated.response ?? '');
      // Finishing starts from the belief as it now stands (beliefAfterRaw stays
      // null until he moves the finish slider), so a rating changed here cannot
      // leave that slider at an old value and record a drop he never reported.
      setCrisis(updated.crisis ?? null);
      setEditing(false);
      setEdit(null);
    } catch (e) {
      setError(e instanceof ApiError ? e.message : 'Could not save those changes.');
    } finally {
      setBusy(false);
    }
  }

  async function remove(): Promise<void> {
    if (!id) return;
    if (!confirm('Delete this record permanently? This cannot be undone.')) return;
    try {
      await api.deleteThoughtRecord(id);
    } catch (e) {
      setError(e instanceof ApiError ? e.message : "Couldn't delete that record.");
      return;
    }
    navigate('/history');
  }

  /*
   * The strongest feeling they recorded, before working through it.
   *
   * `intensityBefore` rather than after: the question is whether the moment was
   * hard, and a record whose whole point is that the feeling came down should
   * not stop offering to talk simply because it worked.
   */
  const peakIntensity = record.emotions.length
    ? Math.max(...record.emotions.map((e) => e.intensityBefore))
    : null;

  return (
    <div className="stack-lg stack">
      <header className="page-header">
        <button
          className="btn btn-ghost btn-compact"
          style={{ marginBottom: 'var(--space-3)', paddingLeft: 0 }}
          onClick={() => navigate('/history')}
        >
          <ArrowLeftIcon /> Back to log
        </button>
        <h1>{formatDate(record.occurredAt)}</h1>
        <p>
          {record.isComplete ? (
            <>
              Finished
              {record.beliefShift !== null && record.beliefShift > 0 && (
                <> · belief dropped {record.beliefShift} points</>
              )}
            </>
          ) : (
            'Not finished yet'
          )}
          {format.key !== 'standard' && ` · ${format.name}`}
        </p>
        {/* Up here with the record rather than down beside Delete. Taking one
            record to an appointment is the reason this exists, and by the time
            you have scrolled past the last column you are leaving the screen,
            not looking for a way to print it — parked next to a permanent
            delete, it is also a control people learn to slide their eyes past.

            Bordered and carrying the same printer icon as the log's version of
            it, because the ghost-link form of this — transparent background,
            inherited colour, no border, no icon — rendered as an ordinary
            sentence and went unnoticed by the person who owned the app for
            three sessions running. */}
        <div className="row" style={{ marginTop: 'var(--space-3)', flexWrap: 'wrap' }}>
          <Link
            className="btn btn-secondary btn-compact"
            to={`/export?record=${encodeURIComponent(record.id)}`}
          >
            <PrinterIcon /> Print or save this one
          </Link>
          {!editing && (
            <button className="btn btn-secondary btn-compact" onClick={startEditing}>
              Change it
            </button>
          )}
        </div>
      </header>

      {crisis && !editing && <CrisisNoticeCard notice={crisis} onDismiss={() => setCrisis(null)} />}

      {editing && edit && (
        <section className="card stack" aria-label="Change this worksheet">
          <h3>Change it</h3>
          <p className="muted">Everything here is yours to change. Nothing is saved until you save it.</p>

          <div className="field">
            <label htmlFor="edit-situation">{format.labels.situation}</label>
            <textarea
              id="edit-situation"
              className="textarea"
              value={edit.situation}
              onChange={(e) => setEdit({ ...edit, situation: e.target.value })}
            />
          </div>

          <div className="field">
            <span className="field-label">What you felt</span>
            <FeelingsEditor emotions={edit.emotions} onChange={(emotions) => setEdit({ ...edit, emotions })} />
          </div>

          <div className="field">
            <label htmlFor="edit-thought">{format.labels.thought}</label>
            <textarea
              id="edit-thought"
              className="textarea"
              value={edit.thought}
              onChange={(e) => setEdit({ ...edit, thought: e.target.value })}
            />
          </div>

          {/* Not said yet until he sets it: a button that jumped to 70 saved 70 as
              his if he saved without moving it. */}
          <div className="field">
            <label htmlFor="edit-belief">{format.beliefLabel}</label>
            <RatingSlider
              id="edit-belief"
              value={edit.beliefBefore}
              suffix="%"
              low="Not at all"
              high="Completely"
              onChange={(v) => setEdit({ ...edit, beliefBefore: v })}
            />
          </div>

          {/* A column shows when this worksheet asks it, or when it already has
              something in it — a draft can fill one the format never asks, and
              what is saved has to be changeable. */}
          {(format.steps.some((st) => st.field === 'evidenceFor') || evidenceFor.length > 0) && (
            <div className="field">
              <label htmlFor="edit-for">{format.labels.evidenceFor}</label>
              <span className="hint">One on each line.</span>
              <textarea
                id="edit-for"
                className="textarea"
                value={edit.evidenceFor}
                onChange={(e) => setEdit({ ...edit, evidenceFor: e.target.value })}
              />
            </div>
          )}
          {(format.steps.some((st) => st.field === 'evidenceAgainst') || evidenceAgainst.length > 0) && (
            <div className="field">
              <label htmlFor="edit-against">{format.labels.evidenceAgainst}</label>
              <span className="hint">One on each line.</span>
              <textarea
                id="edit-against"
                className="textarea"
                value={edit.evidenceAgainst}
                onChange={(e) => setEdit({ ...edit, evidenceAgainst: e.target.value })}
              />
            </div>
          )}

          {distortions.length > 0 && (
            <TrapPicker
              distortions={distortions}
              picked={edit.picked}
              appPicked={edit.appPicked}
              onChange={(next) => setEdit({ ...edit, picked: next.picked, appPicked: next.appPicked })}
            />
          )}

          {record.isComplete && (
            <div className="field">
              <label htmlFor="edit-response">{format.labels.response}</label>
              <textarea
                id="edit-response"
                className="textarea"
                value={edit.response}
                onChange={(e) => setEdit({ ...edit, response: e.target.value })}
              />
            </div>
          )}

          {error && <div className="banner banner-error">{error}</div>}

          <div className="stack-sm stack">
            <button
              className="btn btn-primary btn-block"
              onClick={() => void saveChanges()}
              disabled={busy || !edit.situation.trim() || !edit.thought.trim()}
            >
              {busy ? <span className="spinner" /> : 'Save changes'}
            </button>
            {(!edit.situation.trim() || !edit.thought.trim()) && (
              <p className="faint" style={{ textAlign: 'center' }}>
                What happened and the thought need something in them to save.
              </p>
            )}
            <button
              className="btn btn-ghost btn-block"
              onClick={() => {
                setEditing(false);
                setEdit(null);
                setError(null);
              }}
              disabled={busy}
            >
              Keep it as it was
            </button>
          </div>
        </section>
      )}

      {/* Placed above the columns rather than below them: if this record came
          out of something you said, that is context for everything underneath,
          and it belongs before the summarised version rather than as a footnote
          after it. */}
      {source && (
        <Link className="option linked-source" to={`/journal/${source.id}`}>
          <strong>{source.title ?? 'Your journal entry'}</strong>
          {/* No date here — it is the same day as the header above in all but
              pathological cases, and no verb for how it was captured, because
              an entry can be spoken or typed and "you talked this through" is
              wrong half the time. */}
          <span>The journal entry this came out of — all of it, in your own words.</span>
        </Link>
      )}

      {/* Offered on the person's own numbers, not on a reading of their
          writing — 70 out of 100 is something they said about themselves.
          Placed after the record rather than before it, so it follows the
          work instead of interrupting it. */}
      {/* Earned by the person's own number, never by a model's read of them —
          which is why this one survives at `reflect` and below, and disappears
          only when conversing is switched off entirely. */}
      {canConverse && peakIntensity !== null && peakIntensity >= 70 && (
        <TalkOffer
          reason={{ kind: 'intensity', intensity: peakIntensity ?? 0 }}
          occurredAt={record.occurredAt}
        />
      )}

      {/* The record as it stands, or the form changing it — never both, so
          there is one version of each answer on screen. */}
      {!editing && (
        <>
        <Section title={format.labels.situation}>
          <p className="reading">{record.situation}</p>
        </Section>

        {/* The owner reading their own record — their browser is their clock. */}
        <RecordMeds medications={record.medications} />

        {record.emotions.length > 0 && (
          <Section title="What you felt">
            <div className="stack-sm stack">
              {record.emotions.map((e) => (
                <div className="row-between" key={e.emotion}>
                  <strong>{e.emotion}</strong>
                  <span className="faint">
                    {e.intensityBefore}
                    {e.intensityAfter !== null && e.intensityAfter !== e.intensityBefore && (
                      <> → {e.intensityAfter}</>
                    )}
                  </span>
                </div>
              ))}
            </div>
          </Section>
        )}

        <Section title={format.labels.thought}>
          <p className="reading">“{record.automaticThought}”</p>
          {record.beliefBefore !== null && (
            <p className="faint" style={{ marginTop: 'var(--space-2)' }}>
              Believed {record.beliefBefore}%
              {record.beliefAfter !== null && <> at the time, {record.beliefAfter}% afterwards</>}
            </p>
          )}
        </Section>

        {evidenceFor.length > 0 && (
          <Section title={format.labels.evidenceFor}>
            <ul style={{ paddingLeft: '1.1rem' }}>
              {evidenceFor.map((e, i) => (
                <li key={i} style={{ marginBottom: 'var(--space-1)' }}>{e.text}</li>
              ))}
            </ul>
          </Section>
        )}

        {evidenceAgainst.length > 0 && (
          <Section title={format.labels.evidenceAgainst}>
            <ul style={{ paddingLeft: '1.1rem' }}>
              {evidenceAgainst.map((e, i) => (
                <li key={i} style={{ marginBottom: 'var(--space-1)' }}>{e.text}</li>
              ))}
            </ul>
          </Section>
        )}

        {/*
          The traps on this worksheet. They were on the printed sheet and a share
          link and nowhere here, so the one page he reads never showed them — and
          on a worksheet drafted from a journal entry the model had picked them.
          The owner chose (10 Sep) to keep those, marked: dashed, and said so.
        */}
        {record.distortions.length > 0 && (
          <Section title="Thinking traps">
            {/* Plain text, not chips: chips here are buttons, and these do nothing. */}
            <p>
              {record.distortions.map((key, i) => {
                const app = record.appDistortions.includes(key);
                return (
                  <span key={key}>
                    {i > 0 ? ' · ' : ''}
                    <span className={app ? 'trap-app' : undefined}>
                      {trapNames[key] ?? key.replace(/_/g, ' ')}
                      {app ? ' (app)' : ''}
                    </span>
                  </span>
                );
              })}
            </p>
            {record.appDistortions.some((k) => record.distortions.includes(k)) && (
              <p className="faint" style={{ marginTop: 'var(--space-2)' }}>
                The ones marked (app) were picked by the app when it drafted this worksheet from your journal entry — its
                suggestion, not your answer. They show as the app&rsquo;s picks when printed or shared too.
              </p>
            )}
          </Section>
        )}

        {record.isComplete ? (
          <Section title={format.labels.response}>
            <p className="reading">{record.response}</p>
          </Section>
        ) : (
          <section className="card stack">
            <h3>Finish it now</h3>
            <p className="muted">{lastStep.hint ?? lastStep.label}</p>
            <div className="field">
              <textarea
                className="textarea"
                value={response}
                onChange={(e) => setResponse(e.target.value)}
                placeholder={lastStep.placeholder}
              />
            </div>

            <div className="field">
              {/* Same reason as the creation flow: this form is reached from the
                  log, days later, and the thought is above the fold at best. */}
              <p className="rerate-thought">“{record.automaticThought}”</p>
              <label htmlFor="after">How much do you believe it now?</label>
              <RatingSlider
                id="after"
                value={beliefAfterRaw ?? record.beliefBefore}
                suffix="%"
                low="Not at all"
                high="Completely"
                onChange={setBeliefAfterRaw}
                after={beliefAfterRaw === null ? <span className="faint"> · no change</span> : null}
              />
            </div>

            {record.emotions.map((e) => (
              <div className="field" key={e.emotion}>
                <label htmlFor={`a-${e.emotion}`}>
                  {e.emotion} <span className="faint">was {e.intensityBefore}</span>
                </label>
                {/* Shows where it sits, and that leaving it there means no change —
                    which is what finishing saves. */}
                <RatingSlider
                  id={`a-${e.emotion}`}
                  value={afterIntensities[e.emotion] ?? e.intensityAfter ?? e.intensityBefore}
                  low="Gone"
                  high="As strong as it was"
                  onChange={(v) => setAfterIntensities({ ...afterIntensities, [e.emotion]: v })}
                  after={
                    afterIntensities[e.emotion] === undefined && e.intensityAfter === null ? (
                      <span className="faint"> · no change</span>
                    ) : null
                  }
                />
              </div>
            ))}

            <button
              className="btn btn-primary btn-block"
              onClick={() => void finish()}
              disabled={busy || !response.trim()}
            >
              {busy ? <span className="spinner" /> : 'Save'}
            </button>
            {/* A greyed-out primary button with no stated reason reads as broken.
                Say what it is waiting for. */}
            {!response.trim() && (
              <p className="faint" style={{ textAlign: 'center' }}>
                Write something above and this will save.
              </p>
            )}
          </section>
        )}
        </>
      )}

      <button className="btn btn-ghost btn-ghost-quiet btn-block" onClick={() => void remove()}>
        Delete this record
      </button>
    </div>
  );
}

function Section({ title, children }: { title: string; children: React.ReactNode }): JSX.Element {
  return (
    <section className="stack-sm stack">
      <h2 className="section-head">{title}</h2>
      <div className="card">{children}</div>
    </section>
  );
}

function formatDate(iso: string): string {
  return new Date(iso).toLocaleDateString(undefined, {
    weekday: 'long',
    month: 'long',
    day: 'numeric',
  });
}
